import { Button, Card } from "@rumbledirect/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Input, message, Typography } from "antd";
import { MIME_TYPE_MERGE_PATCH_JSON } from "@rumble-it/common-lib/src/constants/mimeTypes";
import { useViewConfigurationClient } from "../client";

export interface InputField {
  key: string;
  label: string;
  type: string;
  required: boolean;
}

export interface InputCardProps {
  title: string;
  description: string;
  fields: InputField[];
  submitEndpoint: string;
  submitText: string;
}

export const InputCard = ({
  title,
  description,
  fields,
  submitEndpoint,
  submitText,
}: InputCardProps) => {
  const axiosInstance = useViewConfigurationClient();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Record<string, string>>();

  const submitMutation = useMutation({
    mutationFn: async (values: Record<string, string>) => {
      return axiosInstance.patch(submitEndpoint, values, {
        headers: { "Content-Type": MIME_TYPE_MERGE_PATCH_JSON },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["view-configuration"] });
    },
    onError: () => {
      message.error("Er is een fout opgetreden. Probeer het opnieuw.");
    },
  });

  const handleFinish = (values: Record<string, string>) => {
    submitMutation.mutate(values);
  };

  return (
    <Card title={title}>
      <Typography.Paragraph>{description}</Typography.Paragraph>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        {fields.map((field) => (
          <Form.Item
            key={field.key}
            name={field.key}
            label={field.label}
            rules={
              field.required
                ? [
                    {
                      required: true,
                      whitespace: true,
                      message: `${field.label} is verplicht`,
                    },
                  ]
                : []
            }
          >
            <Input type={field.type} />
          </Form.Item>
        ))}
        {submitMutation.isError && (
          <Typography.Paragraph type="danger">
            Er is een fout opgetreden bij het opslaan. Probeer het opnieuw.
          </Typography.Paragraph>
        )}
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitMutation.isPending}
            style={{ width: "100%" }}
          >
            {submitText}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};
